import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { trackEvent } from "@/components/Analytics";
import SuccessCheck from "@/components/SuccessCheck";
import useSEO from "@/hooks/useSEO";

export default function Contact() {
  const { contact } = useSite();
  const [params] = useSearchParams();
  useSEO({
    title: "Contact",
    description: "Contactez AMAR SAHRAOUI Promotion Immobilière à Oran. Téléphone +213 5 61 66 70 41, WhatsApp, email commercial@sahraoui-promotion.com. Bel Gayd, Bir El Djir, Oran, Algérie.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    subject: params.get("ref") ? `Demande d'information — ${params.get("ref")}` : "",
    message: "",
    project_name: params.get("projet") || "",
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      await api.post("/public/contact", form);
      setSuccess(true);
      trackEvent("contact_form_submit", {
        subject: form.subject || "",
        project_name: form.project_name || "",
      });
      setForm({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "", project_name: "" });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Erreur lors de l'envoi.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className="pt-16 pb-12 lg:pt-24 lg:pb-16 border-b atelier-border-soft bg-atelier-deep relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-20" aria-hidden="true" />
        <div className="px-5 lg:px-10 relative z-10">
          <div className="label-tech text-atelier-ink mb-4">— Contact</div>
          <h1 className="font-serif text-5xl lg:text-7xl tracking-tight leading-none max-w-4xl text-atelier-text">
            Parlons de votre <span className="italic text-atelier-ink">projet.</span>
          </h1>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-atelier">
        <div className="px-5 lg:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="label-tech text-atelier-ink mb-4">— Coordonnées</div>
            <h2 className="font-serif text-3xl lg:text-4xl mb-10 leading-tight text-atelier-text">À votre écoute, du dimanche au jeudi.</h2>
            <ul className="space-y-6">
              {contact?.address && (
                <li className="flex gap-4">
                  <MapPin className="w-5 h-5 text-atelier-ink flex-shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <div className="label-tech text-atelier-textFaint mb-1">Adresse</div>
                    <div className="text-atelier-text font-serif">{contact.address}</div>
                  </div>
                </li>
              )}
              {contact?.phone && (
                <li className="flex gap-4">
                  <Phone className="w-5 h-5 text-atelier-ink flex-shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <div className="label-tech text-atelier-textFaint mb-1">Téléphone</div>
                    <a href={`tel:${contact.phone.replace(/\s/g, "")}`} onClick={() => trackEvent("phone_click", { location: "contact_page" })} className="font-serif text-xl text-atelier-text hover:text-atelier-ink transition-colors">{contact.phone}</a>
                  </div>
                </li>
              )}
              {contact?.whatsapp && (
                <li className="flex gap-4">
                  <span className="w-5 h-5 text-atelier-ink flex-shrink-0 mt-1 label-tech text-[0.65rem]">WA</span>
                  <div>
                    <div className="label-tech text-atelier-textFaint mb-1">WhatsApp</div>
                    <a href={`https://wa.me/${contact.whatsapp.replace(/\D/g, "")}`} onClick={() => trackEvent("whatsapp_click", { location: "contact_page" })} target="_blank" rel="noreferrer" className="font-serif text-xl text-atelier-text hover:text-atelier-ink transition-colors" data-testid="contact-whatsapp">{contact.whatsapp}</a>
                  </div>
                </li>
              )}
              {contact?.email && (
                <li className="flex gap-4">
                  <Mail className="w-5 h-5 text-atelier-ink flex-shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <div className="label-tech text-atelier-textFaint mb-1">Email</div>
                    <a href={`mailto:${contact.email}`} onClick={() => trackEvent("email_click", { location: "contact_page" })} className="text-atelier-text hover:text-atelier-ink transition-colors break-all">{contact.email}</a>
                  </div>
                </li>
              )}
              {contact?.hours && (
                <li className="flex gap-4">
                  <Clock className="w-5 h-5 text-atelier-ink flex-shrink-0 mt-1" strokeWidth={1.4} />
                  <div>
                    <div className="label-tech text-atelier-textFaint mb-1">Horaires</div>
                    <div className="text-atelier-text font-serif">{contact.hours}</div>
                  </div>
                </li>
              )}
            </ul>
          </div>

          <div className="md:col-span-7">
            {success ? (
              <div className="atelier-border bg-atelier-ink/5 p-10 text-center" data-testid="contact-success">
                <div className="flex justify-center mb-6"><SuccessCheck size={72} /></div>
                <h3 className="font-serif text-3xl mb-3 text-atelier-text">Message envoyé.</h3>
                <p className="text-atelier-textDim mb-6 font-serif">Merci pour votre demande. Un conseiller AMAR SAHRAOUI vous recontactera sous 24h ouvrées.</p>
                <button onClick={() => setSuccess(false)} className="btn-outline">Envoyer un autre message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="atelier-border bg-atelier-surface/30 p-8 lg:p-10 space-y-5" data-testid="contact-form">
                <div className="label-tech text-atelier-ink">— Formulaire</div>
                <h3 className="font-serif text-2xl text-atelier-text">Demander à être recontacté</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input label="Prénom *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} required testId="contact-first-name" />
                  <Input label="Nom *" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} required testId="contact-last-name" />
                  <Input label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testId="contact-email" />
                  <Input label="Téléphone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="contact-phone" />
                </div>
                <Input label="Sujet" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />
                <Input label="Projet concerné (optionnel)" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} testId="contact-project" />
                <div>
                  <label className="label-tech text-atelier-textFaint mb-2 block">Message *</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    data-testid="contact-message"
                    className="atelier-input w-full"
                  />
                </div>
                {err && <div className="text-sm text-red-400 font-serif">{err}</div>}
                <button type="submit" disabled={sending} className="btn-primary w-full sm:w-auto" data-testid="contact-submit">
                  {sending ? "Envoi…" : (<><Send className="w-3.5 h-3.5" /> Envoyer le message</>)}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {contact?.map_embed_url && (
        <section className="pb-20 bg-atelier">
          <div className="px-5 lg:px-10">
            <div className="aspect-[16/7] atelier-border">
              <iframe src={contact.map_embed_url} title="Carte" className="w-full h-full" loading="lazy" />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testId }) {
  return (
    <div>
      <label className="label-tech text-atelier-textFaint mb-2 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testId}
        className="atelier-input w-full"
      />
    </div>
  );
}
